// components/wizard/ReviewSubmit.jsx
import React, { useState } from 'react';
import { CheckCircle, AlertCircle, Download, FileText } from 'lucide-react';
import RichTextViewer from '../../common/RichTextViewer';
import { showToast } from '../../../utils/toast';

// Files live in the form as uploaded URLs; older drafts may still hold File objects.
const fileUrl = (file) => (typeof file === 'string' ? file : file?.url || '');
const fileLabel = (file) => {
  if (typeof file === 'string') {
    try {
      return decodeURIComponent(file.split('/').pop()) || file;
    } catch (_) {
      return file;
    }
  }
  return file?.name || 'Uploaded file';
};

const ReviewSubmit = ({ formData, onBack }) => {
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = () => {
    setSubmitting(true);
    // Simulate submission
    setTimeout(() => {
      showToast('Manuscript submitted successfully!', 'success');
      window.location.href = '/manuscripts';
    }, 3000);
  };

  const renderSection = (title, content, isValid) => (
    <div className="border rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-medium">{title}</h4>
        {isValid ? (
          <CheckCircle className="w-5 h-5 text-green-500" />
        ) : (
          <AlertCircle className="w-5 h-5 text-red-500" />
        )}
      </div>
      <div className="text-sm text-gray-600">{content}</div>
    </div>
  );

  return (
    <div>
      <h3 className="text-xl font-semibold mb-6">Review & Submit</h3>

      <div className="space-y-4 mb-8">
        {/* Article Type */}
        {renderSection(
          'Article Type',
          `${formData.articleType} - ${formData.discipline}`,
          formData.articleType
        )}

        {/* Files */}
        <div className="border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-medium">Uploaded Files</h4>
            {formData.files.manuscript && formData.files.coverLetter ? (
              <CheckCircle className="w-5 h-5 text-green-500" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-500" />
            )}
          </div>
          <div className="space-y-2">
            {Object.entries(formData.files).map(([key, file]) => (
              file && (
                <div key={key} className="flex items-center text-sm">
                  <FileText className="w-4 h-4 mr-2 text-gray-500" />
                  <span className="capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}:</span>
                  <span className="ml-2 text-gray-600 break-all">{fileLabel(file)}</span>
                  <a
                    href={fileUrl(file)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-2 text-purple-600 hover:text-purple-700"
                  >
                    <Download className="w-4 h-4" />
                  </a>
                </div>
              )
            ))}
          </div>
        </div>

        {/* Title */}
        {renderSection('Title', formData.title, formData.title)}

        {/* Abstract */}
        <div className="border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-medium">Abstract</h4>
            {formData.abstract ? (
              <CheckCircle className="w-5 h-5 text-green-500" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-500" />
            )}
          </div>
          {formData.abstract &&
          <RichTextViewer content={formData.abstract} />
        }
        </div>

        {/* Keywords */}
        {renderSection(
          'Keywords',
          formData.keywords.filter(k => k).join('; '),
          formData.keywords.filter(k => k).length >= 3
        )}

        {/* Authors */}
        <div className="border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-medium">Authors</h4>
            <CheckCircle className="w-5 h-5 text-green-500" />
          </div>
          <div className="space-y-2">
            <p className="text-sm font-medium">Corresponding Author: </p>
            {formData.authors.map((author, index) => (
              <p key={index} className="text-sm text-gray-600">
                {author.prefix} {author.firstName} {author.lastName} - {author.email}
              </p>
            ))}
          </div>
        </div>

        {/* Reviewers */}
        <div className="border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-medium">Suggested Reviewers</h4>
            <CheckCircle className="w-5 h-5 text-green-500" />
          </div>
          <div className="space-y-2">
            {formData.reviewers.length > 0 ? (
              formData.reviewers.map((reviewer, index) => (
                <p key={index} className="text-sm text-gray-600">
                  {reviewer.fullName} - {reviewer.email}
                </p>
              ))
            ) : (
              <p className="text-sm text-gray-500">No reviewers suggested</p>
            )}
          </div>
        </div>

        {/* Disclosures */}
        <div className="border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-medium">Disclosures</h4>
            {Object.values(formData.disclosures).every(Boolean) ? (
              <CheckCircle className="w-5 h-5 text-green-500" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-500" />
            )}
          </div>
          <div className="text-sm text-gray-600">
            All disclosures have been confirmed
          </div>
        </div>
      </div>

      {/* <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-8 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
        >
          Back
        </button>
        
        <button
          onClick={handleSubmit}
          disabled={!Object.values(formData.disclosures).every(Boolean) || submitting}
          className={`px-8 py-3 rounded-lg flex items-center ${
            Object.values(formData.disclosures).every(Boolean) && !submitting
              ? 'bg-green-600 text-white hover:bg-green-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          {submitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Submitting...
            </>
          ) : (
            'Submit Manuscript'
          )}
        </button>
      </div> */}
    </div>
  );
};

export default ReviewSubmit;
